import { debounce } from 'debounce';
import hunt from 'huntjs';
import { ResizeObserver as Polyfill } from '@juggle/resize-observer';
const ResizeObserver = window.ResizeObserver || Polyfill;

const defaultPosterVideoInitDelay = 5000; // the delay, once the poster image has loaded, before we load the video iframe

class HeroVideoVimeo {

    constructor(el) {
        this.wrapper = el;
        this.controls = this.wrapper.querySelector('.hero--video--control');
        this.loadingSprite = this.wrapper.querySelector('.hero--video-loading-sprite');
        this.playButton = this.controls.querySelector('button.play');
        this.soundButton = this.controls.querySelector('button.sound');
        this.started = false;
        this.userPaused = false;
        this.muted = true;
        this.hasPoster = false;
        this.crossfadePoster = false;
        this.videoInitDelay = defaultPosterVideoInitDelay;
        this.inner = {
            poster : this.wrapper.querySelector('.hero--poster-image'),
            posterImg : this.wrapper.querySelector('.hero--poster-image img'),
            el : this.wrapper.querySelector('.hero--video-inner:first-child'),
            top : 0,
            left : 0,
            width : 0,
            height : 0
        }
        // are we dealing with a poster image?
        if(this.inner.posterImg != null) {
            this.hasPoster = true;
            this.inner.el.classList.add('has-poster');
            // crossfade?
            this.crossfadePoster = this.inner.poster.dataset.crossfade == 'true';
            if(this.crossfadePoster) this.inner.el.classList.add('crossfade-poster');
            // custom init delay value?
            let delay = parseInt(this.inner.poster.dataset.delay);
            if(delay > 0) this.videoInitDelay = delay;
        }
        this.ratioHeightByWidth = 9 / 16;
        this.ratioWidthByHeight = 16 / 9;
        // find video id in data attribute
        this.videoId = this.wrapper.dataset.heroVideoId;
        // add resize observer to hero element
        this.ro = new ResizeObserver(entries => {
            for (let entry of entries) {
                const cr = entry.contentRect;
                debounce(this.resize(Math.ceil(cr.width), Math.ceil(cr.height)), 200);
            }
        });
        this.ro.observe(this.wrapper);
        // init video based on poster image
        if(this.videoId != null) {
            if(this.hasPoster) {
                if (this.inner.posterImg.complete) {
                    setTimeout(() => {
                        this.loadPlayer();
                    }, this.videoInitDelay);
                } else {
                    let _this = this;
                    this.inner.posterImg.addEventListener('load', () => {
                        setTimeout(() => {
                            _this.loadPlayer();
                        }, _this.videoInitDelay);
                    }, {once: true});
                }
            } else {
                this.loadPlayer();
            }
        }
    }

    async loadPlayer() {
        let script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://player.vimeo.com/api/player.js'; 
        script.async = true;
        document.head.appendChild(script);
        script.onload = () => {
            this.initVideo();
        };
    }

    initVideo() {
        // add video
        this.video = new Vimeo.Player(this.inner.el, {
            id: this.videoId,
            loop: true,
            background: true
        });
        let thisInstance = this;
        // hook up control button?
        if(this.playButton !== null) {
            this.playButton_play = this.playButton.querySelector('.hero--video--control--play');
            this.playButton_pause = this.playButton.querySelector('.hero--video--control--pause');
            this.playButton_pause.removeAttribute('hidden');
            // add behaviours
            this.video.on('play', ()=> {
                this.playButton_play.setAttribute('aria-hidden', true);
                this.playButton_pause.removeAttribute('aria-hidden');
                // fade video in when it starts playback for the first time
                if(!this.started) {
                    this.inner.el.classList.add('reveal');
                    this.controls.classList.add('reveal');
                    if(this.loadingSprite != null) this.loadingSprite.classList.add('hidden');
                    this.started = true;
                }
            });
            this.video.on('pause', ()=> {
                this.playButton_play.removeAttribute('aria-hidden');
                this.playButton_pause.setAttribute('aria-hidden', true);
            });
            this.playButton.onclick = () => {
                thisInstance.video.getPaused().then(function(paused) {
                    if(!paused) {
                        thisInstance.video.pause();
                        thisInstance.userPaused = true;
                    } else {
                        thisInstance.video.play();
                        thisInstance.userPaused = false;
                    }
                });
            };
        }
        // hook up optional sound control button?
        if(this.soundButton !== null) {
            this.soundButton_on = this.soundButton.querySelector('.hero--video--control--sound-on');
            this.soundButton_off = this.soundButton.querySelector('.hero--video--control--sound-off');
            this.soundButton_off.removeAttribute('hidden');
            this.soundButton.onclick = () => {
                if(thisInstance.muted) {
                    thisInstance.video.setMuted(false).then(() => {
                        thisInstance.soundButton_on.setAttribute('aria-hidden', true);
                        thisInstance.soundButton_off.removeAttribute('aria-hidden');
                        thisInstance.muted = false;
                    });
                } else {
                    thisInstance.video.setMuted(true).then(() => {
                        thisInstance.soundButton_on.removeAttribute('aria-hidden');
                        thisInstance.soundButton_off.setAttribute('aria-hidden', true);
                        thisInstance.muted = true;
                    });
                }
            }
        }
        // add hunt to pause video if scrolled offscreen
        let fixedWrapper = document.querySelector('.hero--fixed-visual--wrapper');
        this.hunt = new hunt(fixedWrapper != null ? fixedWrapper : this.wrapper, {
            leave: function () {
                thisInstance.video.getPaused().then(function(paused) {
                    if(!paused) thisInstance.video.pause();
                });
            },
            enter: function () {
                thisInstance.video.getPaused().then(function(paused) {
                    if(paused && !thisInstance.userPaused) thisInstance.video.play();
                });
            },
            persist: true
        });
    }

    resize(getWidth, getHeight) {
        let ratio = getHeight/getWidth;
        if(ratio < this.ratioHeightByWidth) {
            // use full width
            this.inner.width = getWidth;
            this.inner.height = Math.ceil(getWidth * this.ratioHeightByWidth);
            this.inner.top = ((getHeight - this.inner.height) / 2).toFixed(1);
            this.inner.left = 0;
        } else {
            // use full height
            this.inner.height = getHeight;
            this.inner.width = Math.ceil(getHeight * this.ratioWidthByHeight);
            this.inner.top = 0;
            this.inner.left = ((getWidth - this.inner.width) / 2).toFixed(1);
        }
        this.inner.el.style = `top: ${this.inner.top}px; left: ${this.inner.left}px; width: ${this.inner.width}px; height: ${this.inner.height}px;`;
    }

}

// export the default function to create
export function createFrom(wrappers) {
    wrappers.forEach((node) => {
        // if node is an element
        if (node.nodeType === 1) {
            new HeroVideoVimeo(node);
        }
    });
}